import { useTranslation } from "react-i18next";
import { brand } from "../../../../config/theme";

export interface BulkAction {
  id: string;
  label: string;
  icon: string;
  onClick: () => void;
  variant: "primary" | "success" | "warning" | "danger";
  disabled?: boolean;
}

interface BulkActionBarProps {
  selectedCount: number;
  actions: BulkAction[];
  onClearSelection: () => void;
  isDarkMode: boolean;
}

export function BulkActionBar({
  selectedCount,
  actions,
  onClearSelection,
  isDarkMode,
}: BulkActionBarProps) {
  const { t } = useTranslation("resellerAdmin");

  if (selectedCount === 0) return null;

  const getButtonClasses = (variant: BulkAction["variant"]) => {
    switch (variant) {
      case "primary":
        return "bg-primary-500 hover:bg-primary-400 text-gray-900";
      case "success":
        return "bg-green-500 hover:bg-green-400 text-white";
      case "warning":
        return "bg-orange-500 hover:bg-orange-400 text-white";
      case "danger":
        return "bg-red-500 hover:bg-red-400 text-white";
      default:
        return "bg-primary-500 hover:bg-primary-400 text-gray-900";
    }
  };

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 ${
        isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200"
      } border-t shadow-lg`}
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Left side - Selection info */}
        <div className="flex items-center gap-4">
          <button
            onClick={onClearSelection}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode
                ? "hover:bg-dark-card text-gray-400 hover:text-white"
                : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
            }`}
            title={t("bulkActions.clearSelection")}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="flex items-center gap-2">
            <span
              className="px-2 py-1 rounded-full text-sm font-medium"
              style={{ backgroundColor: brand.primary, color: brand.accent }}
            >
              {selectedCount}
            </span>
            <span className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
              {t("bulkActions.itemsSelected", { count: selectedCount })}
            </span>
          </div>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-2">
          {actions.map((action) => (
            <button
              key={action.id}
              onClick={action.onClick}
              disabled={action.disabled}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${getButtonClasses(
                action.variant
              )}`}
            >
              <i className={`${action.icon} text-base`}></i>
              <span className="hidden sm:inline">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
